import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useQuote } from '../context/QuoteContext';
import { toast } from '../hooks/use-toast';
import { quoteService } from '../services/api';

const RequestQuote = () => {
  const { quoteItems, clearQuote } = useQuote();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    deliveryAddress: '',
    decorationMethod: '',
    additionalNotes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const quoteData = {
        company_name: formData.companyName,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        delivery_address: formData.deliveryAddress,
        decoration_method: formData.decorationMethod,
        additional_notes: formData.additionalNotes,
        items: quoteItems.map(item => ({
          product_code: item.product_code,
          product_name: item.name,
          quantity: item.quantity,
          image_url: item.image_url
        }))
      };

      await quoteService.submitQuote(quoteData);
      setLoading(false);
      setSubmitted(true);
      clearQuote();
      toast({
        title: 'Quote Request Submitted!',
        description: 'Our team will contact you within 24 hours.',
      });
    } catch (error) {
      setLoading(false);
      toast({
        title: 'Error',
        description: 'Failed to submit quote. Please try again.',
        variant: 'destructive'
      });
    }
  };

  if (quoteItems.length === 0 && !submitted) {
    navigate('/products/all');
    return null;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-2xl w-full mx-4">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Quote Request Submitted!
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for your quote request. Our team at ProCloud Creative will review your requirements and contact you within 24 hours with a detailed quotation.
            </p>
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-6 mb-8">
              <p className="text-sm text-pink-900">
                <strong>What happens next?</strong><br />
                1. Our team reviews your product selections<br />
                2. We prepare a detailed quote with pricing options<br />
                3. We'll contact you via email or phone<br />
                4. You can discuss any modifications or questions
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate('/')} size="lg">
                Back to Home
              </Button>
              <Button onClick={() => navigate('/products/all')} variant="outline" size="lg">
                Browse More Products
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-pink-900 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Request a Quote</h1>
          <p className="text-pink-100">
            Fill in your details and we'll get back to you with a detailed quote
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quote Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="companyName">Company Name *</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        placeholder="Your Company Pty Ltd"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactName">Contact Name *</Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="04XX XXX XXX"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="deliveryAddress">Delivery Address *</Label>
                    <Textarea
                      id="deliveryAddress"
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleChange}
                      required
                      placeholder="Street address, city, state, postcode"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="decorationMethod">Preferred Decoration Method</Label>
                    <select
                      id="decorationMethod"
                      name="decorationMethod"
                      value={formData.decorationMethod}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="">Select a method (optional)</option>
                      <option value="screen-print">Screen Printing</option>
                      <option value="embroidery">Embroidery</option>
                      <option value="laser-engraving">Laser Engraving</option>
                      <option value="pad-print">Pad Printing</option>
                      <option value="digital-print">Digital Printing</option>
                      <option value="not-sure">Not Sure - Recommend</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="additionalNotes">Additional Notes or Requirements</Label>
                    <Textarea
                      id="additionalNotes"
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleChange}
                      placeholder="Timeline, specific colors, budget considerations, or any other details..."
                      rows={4}
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Quote Request'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Quote Items Summary */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">Your Selected Products</h2>
                <div className="space-y-4 mb-6">
                  {quoteItems.map((item) => (
                    <div key={item.id} className="border-b pb-3">
                      <div className="flex gap-3">
                        <div
                          className="w-16 h-16 rounded bg-cover bg-center flex-shrink-0"
                          style={{ backgroundImage: `url(${item.image})` }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{item.name}</h4>
                          <p className="text-xs text-gray-600">{item.id}</p>
                          <p className="text-sm font-medium mt-1">Qty: {item.quantity}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <p className="text-xs text-pink-900">
                    <strong>Need help?</strong><br />
                    Call us at <strong>1300 722 659</strong> for immediate assistance.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestQuote;
